import {
  httpBatchLink,
  splitLink,
  httpLink,
  HTTPLinkOptions,
  HTTPBatchLinkOptions,
} from "@trpc/client";
import { createTRPCNext } from "@trpc/next";
import { type inferReactQueryProcedureOptions } from "@trpc/react-query";
import type { inferRouterInputs, inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "@zapier/bff";
import { API_ROUTE } from "@zapier/utils";
import { toastFriendlyErrorMessage } from "../utils/getFriendlyErrorMessage";

export const getBaseUrl = () => {
  if (typeof window !== "undefined") return "";
  if (process.env.NEXT_PUBLIC_ZAPIER_ORIGIN)
    return process.env.NEXT_PUBLIC_ZAPIER_ORIGIN;
  return process.env.BASE_URL;
};

// infer the types for AppRouter
export type ReactQueryOptions = inferReactQueryProcedureOptions<AppRouter>;
export type RouterInputs = inferRouterInputs<AppRouter>;
export type RouterOutputs = inferRouterOutputs<AppRouter>;

const defaultLinkOpts: HTTPLinkOptions<any> & HTTPBatchLinkOptions<any> = {
  /**
   * If you want to use SSR, you need to use the server's full URL
   * @link https://trpc.io/docs/ssr
   **/
  url: `${getBaseUrl()}${API_ROUTE}`,
  fetch(url, options) {
    return fetch(url, {
      ...options,
      credentials: "include",
    });
  },
};

// use httpBatchLink and group multiple requests together
const defaultBatchLink = httpBatchLink({
  ...defaultLinkOpts,
  // max URL length to avoid HTTP 413/414 error
  maxURLLength: 2083,
});

// Conditional link for allowing individual procedures to force use of
// POST requests to avoid URL length limits
const conditionalPostRequestLink = splitLink({
  condition(op) {
    // splitLink allows us to conditionally use a different link based on the operation.
    return Boolean(op?.context?.forcePost);
  },
  // if context.forcePost is true, use httpLink as a POST request and skip batching
  true: httpLink({
    ...defaultLinkOpts,
    // force POST requests
    methodOverride: "POST",
  }),
  false: defaultBatchLink,
});

// Conditional link for skipping batching to tune performance on certain critical procedures
const conditionalSkipBatchingLink = splitLink({
  condition(op) {
    // splitLink allows us to conditionally use a different link based on the operation.
    return Boolean(op?.context?.skipBatching);
  },
  // if context.skipBatching is true, use httpLink and skip batching
  true: httpLink({
    ...defaultLinkOpts,
  }),
  false: conditionalPostRequestLink,
});

export const trpc = createTRPCNext<AppRouter>({
  config() {
    return {
      links: [conditionalSkipBatchingLink],
      queryClientConfig: {
        defaultOptions: {
          queries: {
            // default to 20s to avoid excessive refetches
            staleTime: 1000 * 20,
          },
          mutations: {
            onError(error) {
              console.error("Error:", error);
              toastFriendlyErrorMessage(error);
            },
          },
        },
      },
    };
  },
  /**
   * @link https://trpc.io/docs/ssr
   **/
  ssr: false,
});
